<script lang="ts" setup>
import { ElMessage } from 'element-plus'
import debounce from 'lodash/debounce'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, onMounted, ref, Ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import BaseObserver from '@shared/components/BaseObserver.vue'
import PictureInPictureWindow from '@shared/components/VideoChat/PictureInPictureWindow.vue'
import { useChatAutoScroll } from '@shared/composables/use-chat-auto-scroll'
import { useInitializeAudio } from '@shared/composables/use-initialize-audio'
import { AttachmentContentTypes } from '@shared/models/enums/quick-forms'
import {
  AcResults,
  FeedbackForm,
} from '@shared/models/interfaces/conversations'
import { StoreChatMessages } from '@shared/models/interfaces/messages'
import { IMessage } from '@shared/models/proto/base/messages'
import { Nullable } from '@shared/models/types/primary'
import { useTypingIndicatorsStore } from '@shared/store/typing-indicators'
import { useVideoCallConfigsStore } from '@shared/store/video-call-configs'
import { useChatInputActions } from '@shared/utils/composables/chat-messages'
import { useFetchData } from '@shared/utils/composables/fetch-data'
import { isNewSender } from '@shared/utils/helpers/chat/is-same-sender'
import { isMobileDevice } from '@shared/utils/helpers/is-mobile-device'
import { isPending } from '@shared/utils/helpers/request-status'

import { Attachment } from '@protos/chat/attachment_pb'
import { MessageType } from '@protos/chat/message_type_pb'
import { ConversationStatus } from '@protos/conversation_enums_pb'

import ChatAiInput from '@pc/components/ChatBotInput/ChatAiInput.vue'
import QuickReplyItem from '@pc/components/ChatBotInput/components/QuickReplyItem.vue'
import ChatMessage from '@pc/components/ChatMessage.vue'
import { useCreatePatientConversation } from '@pc/composables/create-conversation'
import { useAwayMessageConfigurator } from '@pc/composables/use-away-message-configurator'
import { useChatMessages } from '@pc/composables/use-chat-messages'
import { useMessagePayload } from '@pc/composables/use-message-payload'
import { isFormSubmission } from '@pc/helpers/is-form-submission'
import routeNames from '@pc/router/route-names'
import { useChatWidgetsStore } from '@pc/store/chat-widgets'
import { useConversationsStore } from '@pc/store/conversations'
import { useMessagesStore } from '@pc/store/messages'
import { useQuickFormsStore } from '@pc/store/quick-forms'
import { useRtcSessionsStore } from '@pc/store/rtc-sessions'
import { useSessionStore } from '@pc/store/session'
import { useSocketStore } from '@pc/store/socket'
import { useVideoCallStore } from '@pc/store/video-call'

const TypingIndicator = defineAsyncComponent(
  () => import('@shared/components/TypingIndicator.vue')
)
const AwayMessageConfigurator = defineAsyncComponent(
  () => import('@pc/components/AwayMessageConfigurator.vue')
)
const ChatBotInput = defineAsyncComponent(
  () => import('@pc/components/ChatBotInput/ChatBotInput.vue')
)
const ChatPatientInput = defineAsyncComponent(
  () => import('@pc/components/ChatPatientInput/ChatPatientInput.vue')
)
const CallIncoming = defineAsyncComponent(
  () => import('@pc/components/CallIncoming.vue')
)
const VideoCall = defineAsyncComponent(
  () => import('@pc/components/VideoCall.vue')
)
const AnimatedLoadingText = defineAsyncComponent(
  () => import('@shared/components/AnimatedLoadingText.vue')
)
const VideoCallFeedbackForm = defineAsyncComponent(
  () => import('@shared/components/VideoChat/VideoCallFeedbackForm.vue')
)

const props = withDefaults(defineProps<{ isConversationLoading: boolean }>(), {
  isConversationLoading: false,
})

const chatFooter = ref()

const { t } = useI18n()
const route = useRoute()

const { fetchData: fetchMessages, isLoading: isFetchingMessages } =
  useFetchData()

const conversationsStore = useConversationsStore()
const { currentConversation } = storeToRefs(conversationsStore)

const useTypingStore = useTypingIndicatorsStore()
const socketStore = useSocketStore()
const chatWidgetsStore = useChatWidgetsStore()

const videoCallConfigsStore = useVideoCallConfigsStore()
const { isVideoChatMode, roomId: videoChatRoomId } = storeToRefs(
  videoCallConfigsStore
)

const videoCallStore = useVideoCallStore()
const { isCallIncomingShown, isFeedbackDialogOpened } =
  storeToRefs(videoCallStore)

const conversationParticipants = computed(
  () => currentConversation.value?.participants ?? []
)

const isScreenSharingMode = computed(
  () => videoCallConfigsStore.isScreenSharingMode
)

const isTyping = computed(() => {
  const id = currentConversation.value?.id
  return id ? useTypingStore.getTypingStatus(id) : false
})

const messagesStore = useMessagesStore()

const {
  messages,
  lastVideoCallRoomId,
  lastMessageIsVideoCallInvitation,
  lastMessageIsScreenShareInvitation,
} = storeToRefs(messagesStore)
const quickFormsStore = useQuickFormsStore()

const sessionStore = useSessionStore()
const { chatProps } = storeToRefs(sessionStore)
const { isChannelsConnected, isSocketLoading } = storeToRefs(socketStore)

const rtcSessionsStore = useRtcSessionsStore()

const { startOverConversation } = useCreatePatientConversation()
const { isIphoneKeyboardOpened } = useChatInputActions()
const { botPayload } = useMessagePayload()

const {
  sendUserMessage,
  errorMessage,
  isLastMessage,
  isLoading: isSendMessageLoading,
  messagesList,
  isAnimatedMessage,
  isAiAssigned,
  isLastMessageAssigningToAI,
  starters,
} = useChatMessages()

const chatContainer = ref<Nullable<HTMLElement>>(null)

const conversationCantAcceptNewMessages = ref(false)
const selectedOption = ref<Nullable<string>>(null)

const setSelected = (value: Nullable<string>) => {
  selectedOption.value = value
}

const keyboardOpenedChatView = computed(() => {
  const SMALL_MESSAGE_LIMIT = 8
  const isMessageContainerSmall =
    messagesList.value?.length < SMALL_MESSAGE_LIMIT
  // We apply these changes only for iOS if the user has a lack of messages, and when the keyboard is opened, the system should show messages in the screen area
  return isIphoneKeyboardOpened.value && isMessageContainerSmall
})

const { scrollToBottom } = useChatAutoScroll(
  chatContainer,
  messages as Ref<StoreChatMessages>
)

const isConversationPage = computed(() => route.name == routeNames.conversation)

const showConnectionIndicator = computed(() => {
  return (
    isConversationPage.value &&
    !isChannelsConnected.value &&
    !isSocketLoading.value
  )
})

const isKioskModeEnabled = computed(() => chatWidgetsStore.isKioskModeEnabled)

// Show initial skeleton loading only for the first page of messages
const isInitialLoading = computed(
  () =>
    (isPending(messages.value) && !messages.value.data?.meta?.page) ||
    props.isConversationLoading
)

const { isAwayMessageShown } = useAwayMessageConfigurator()

const isShowStartOverButton = computed(
  () =>
    isKioskModeEnabled.value &&
    currentConversation.value?.status === ConversationStatus.CLOSE
)

const currentWidget = computed(() => chatProps.value?.chatWidget)
const maxSize = computed(() => currentWidget.value?.maximumAttachmentSize)
const allowedTypes = computed(
  () => currentWidget.value?.restrictAttachmentTypes
)

const routeConversationId = computed(() => Number(route.params.conversationId))

const isInputHide = computed(() => {
  return (
    isMobileDevice() &&
    isVideoChatMode.value &&
    !videoCallConfigsStore.isPictureInPictureMode
  )
})

const isPaginationDisabled = computed(() => {
  const { meta } = messages.value.data ?? {}

  return (
    meta?.page === meta?.totalPages ||
    isFetchingMessages.value ||
    isPending(messages.value)
  )
})

const isScheduler = computed(() => {
  const isSubmittedQuickForm = isFormSubmission(
    currentConversation.value?.lastMessage?.attachments[0]?.payload
  )

  return (
    currentConversation.value?.lastMessage?.messageType ===
      MessageType.SCHEDULER && !isSubmittedQuickForm
  )
})

const isGpt = computed(() => {
  const isGptCase = botPayload.value?.option?.case === 'gpt'

  return (
    currentConversation.value?.status === ConversationStatus.ASSIGNED_TO_BOT &&
    isGptCase
  )
})

const optionClickEventHandler = (option: string) => {
  sendUserMessage({
    files: [],
    isMessageToAIAgent: true,
    message: {
      text: option,
      messageType: MessageType.TEXT,
      attachments: [],
    },
  })
}

const markMessageRead = (message: IMessage) => {
  const time = new Date().toISOString()
  socketStore.pushMarkRead(message.conversationId, message.id, time)
}

const recallMessage = (message: IMessage) => {
  const time = new Date().toISOString()
  socketStore.pushMarkRecalled(message.conversationId, message.id, time)
}

const stopGpt = () => {
  socketStore.pushStopQuestioningEvent(currentConversation.value?.id ?? 0)
}

const onContainerScroll = debounce(async () => {
  if (isPaginationDisabled.value) {
    return
  }

  const { meta } = messages.value.data ?? {}

  await fetchMessages({
    async onFetchDataCallback() {
      // Container can't scroll if there is no container. This is just for TypeScript.
      if (!chatContainer.value) {
        return
      }

      // Container has visible scrollbar
      // On tall screens, the first page of messages might not fill the whole chat window
      const containerCanScroll =
        chatContainer.value.scrollHeight > chatContainer.value.clientHeight

      await messagesStore.fetchMessages({
        conversationId: currentConversation.value?.id ?? 0,
        page: (meta?.page ?? 0) + 1,
        perPage: 15,
      })

      if (!containerCanScroll) {
        return await scrollToBottom()
      }

      if (containerCanScroll && chatContainer.value.scrollTop === 0) {
        // If user is scrolled to the top, scroll them down so the observer doesn't trigger again
        // Otherwise we can just keep them where they are

        // Can't use nextTick here because browser does not respect scrollTop assignment when content changes
        // Namely, SystemMessageBubble component mounts and "pushes" the content down and ends up above the visible area
        // With setTimeout we can (hopefully) wait for subchildren to mount too

        setTimeout(() => {
          if (chatContainer.value) {
            chatContainer.value.scrollTop = 40
          }
        }, 50)
      }
    },
  })
}, 1000)

const scrollIfLastMessageOrFirstPage = (isLastMessage: boolean) => {
  if (isLastMessage || messages.value.data?.meta?.page === 1) {
    scrollToBottom()
  }
}

const onTyping = (isTyping: boolean) => {
  if (routeConversationId.value) {
    socketStore.pushIsTyping(routeConversationId.value, isTyping)
  }
}

const sendAcResults = (acResults: AcResults) => {
  const text = acResults.allPassed ? 'Test passed' : 'Test failed'
  sendUserMessage({
    files: [],
    message: {
      messageType: MessageType.TEXT,
      text,
      attachments: [
        new Attachment({
          contentType: AttachmentContentTypes.QuickTestResults,
          inline: JSON.stringify(acResults),
        }),
      ],
    },
  })
}

const { isLoading, fetchData } = useFetchData()

const closeFeedbackDialog = () => {
  isFeedbackDialogOpened.value = false
}

const onSubmitFeedback = (payload: FeedbackForm) => {
  const roomId = videoChatRoomId.value
  if (!roomId) {
    return
  }
  fetchData({
    async onFetchDataCallback() {
      await rtcSessionsStore.updateRtcFeedback({
        roomId,
        data: {
          ...payload,
          requestedAt: new Date().toISOString(),
        },
      })

      ElMessage.success(t('chat.video.feedback.messages.success'))
      closeFeedbackDialog()
      videoCallConfigsStore.setScreenSharingMode(false)
    },
    onErrorCatchCallback: () => {
      ElMessage.error(t('chat.video.feedback.messages.fail'))
    },
  })
}

const onDecllineCall = () => {
  isCallIncomingShown.value = false
  if (!currentWidget.value?.isPostVideoCallFeedbackEnabled) {
    videoCallConfigsStore.setScreenSharingMode(false)
  }
}

watch(isLastMessageAssigningToAI, async (val) => {
  messagesStore.isShowStarters = val
  if (val) {
    await messagesStore.fetchStarters({
      conversationId: String(currentConversation.value?.id ?? 0),
    })
  }
})

watch(
  () => currentConversation.value?.status,
  () => (conversationCantAcceptNewMessages.value = false)
)

watch(lastMessageIsVideoCallInvitation, (value) => {
  if (value && !isVideoChatMode.value) {
    videoCallConfigsStore.setScreenSharingMode(false)
    isCallIncomingShown.value = true
  }
})

watch(lastMessageIsScreenShareInvitation, (value) => {
  if (value && !isVideoChatMode.value) {
    videoCallConfigsStore.setScreenSharingMode(true)
    isCallIncomingShown.value = true
  }
})

watch(isVideoChatMode, (value) => {
  isCallIncomingShown.value = false

  if (!value) {
    isFeedbackDialogOpened.value = Boolean(
      currentWidget.value?.isPostVideoCallFeedbackEnabled
    )
    if (!isFeedbackDialogOpened.value) {
      videoCallConfigsStore.removeRoomData()
    }
  }
})
watch(lastVideoCallRoomId, (value) => {
  if (value) {
    videoChatRoomId.value = value
  }
})

watch(isCallIncomingShown, (value) => {
  if (value) {
    closeFeedbackDialog()
  }
})

watch(isTyping, (value) => {
  if (value) {
    scrollToBottom()
  }
})

const { initializeAudio } = useInitializeAudio()

defineExpose({ scrollToBottom })

onMounted(() => {
  initializeAudio()
  videoCallStore.restoreVideoCallFromCache()
})
</script>

<template>
  <el-skeleton
    animated
    :loading="isInitialLoading"
    class="tw-flex-grow tw-px-5"
    :count="4"
  >
    <template #template>
      <div class="tw-mt-5">
        <div class="tw-flex tw-flex-row tw-items-center tw-mb-2">
          <el-skeleton-item variant="circle" class="tw-mr-2 tw-w-8 tw-h-8" />
          <el-skeleton-item class="tw-block tw-h-4 tw-w-36" variant="text" />
        </div>
        <el-skeleton-item class="tw-block tw-h-6 tw-w-60" variant="rect" />

        <div class="tw-flex tw-flex-row tw-items-center tw-justify-end tw-mb-2">
          <div
            class="tw-flex tw-flex-row tw-items-center tw-justify-end tw-w-48"
          >
            <el-skeleton-item
              class="tw-block tw-ml-auto tw-h-4 tw-w-36"
              variant="rect"
            />
            <el-skeleton-item
              variant="circle"
              class="tw-pl-2 tw-ml-auto tw-w-8 tw-h-8"
            />
          </div>
        </div>
        <el-skeleton-item
          class="tw-block tw-ml-auto tw-h-6 tw-w-60"
          variant="text"
        />
      </div>
    </template>

    <div
      v-show="currentConversation?.status !== ConversationStatus.INVITED"
      ref="chatContainer"
      class="tw-overflow-auto tw-flex tw-flex-col tw-flex-grow"
      data-chat-container
      :class="{
        'tw-justify-end': keyboardOpenedChatView,
      }"
    >
      <BaseObserver
        v-if="
          !isFetchingMessages && !isPending(messages) && !isConversationLoading
        "
        class="tw-mt-1"
        @intersected="onContainerScroll"
      />

      <AnimatedLoadingText
        v-else
        class="tw-font-bold tw-text-base tw-pt-2"
        :text="t('common.loading')"
        data-animated-loading-text
      />

      <ChatMessage
        v-for="(message, index) in messagesList"
        :key="message.id"
        :message="message"
        :conversation="currentConversation"
        :has-branding="Boolean(sessionStore.chatProps?.chatWidget?.branding)"
        :is-last-message="isLastMessage(message)"
        :is-new-sender="
          isNewSender({
            previousMessage: messagesList[index - 1],
            currentMessage: message,
          })
        "
        :fetch-quick-form="quickFormsStore.fetchQuickFormById"
        :is-me="sessionStore.isMe(`${message.sender?.id}`)"
        :index="index"
        :is-animated-message="isAnimatedMessage(message)"
        :items="messagesList"
        :is-typing="isTyping"
        @send-ac-results="sendAcResults"
        @mark-read="markMessageRead"
        @recall="recallMessage"
        @scroll-to-bottom="
          scrollIfLastMessageOrFirstPage(isLastMessage(message))
        "
      />

      <!--      <div-->
      <!--        class="app-space-for-ai"-->
      <!--        data-space-block-->
      <!--        :style="{ minHeight: minHeightForAiMessageSpace }"-->
      <!--      />-->

      <TypingIndicator v-if="!isAiAssigned && isTyping" />
    </div>

    <div ref="chatFooter">
      <AwayMessageConfigurator v-if="isAwayMessageShown" />
      <AppText
        v-if="showConnectionIndicator"
        color="error"
        as="div"
        size="xl"
        weight="bold"
        class="tw-text-center tw-py-3"
        data-socket-disconected
      >
        {{ t('chat.labels.socketDisconnectedIssue') }}
      </AppText>

      <div v-if="isGpt" data-ai-block>
        <el-row
          v-if="isLastMessageAssigningToAI"
          align="middle"
          data-starters
          justify="center"
        >
          <div class="tw-flex tw-flex-col tw-w-full">
            <QuickReplyItem
              v-for="starter in starters"
              :key="starter"
              :option="starter"
              :active="starter === selectedOption"
              @send-message="optionClickEventHandler"
              @set-active="setSelected"
            />
          </div>
        </el-row>

        <div v-else class="tw-text-center">
          <AppButton type="primary" plain data-stop-gpt @click="stopGpt">
            {{ t('pt.stopGpt') }}
          </AppButton>
        </div>

        <ChatAiInput
          :loading="isSendMessageLoading"
          :conversation-id="currentConversation?.id ?? 0"
          @send-message="sendUserMessage"
          @loaded="scrollToBottom"
        />
      </div>

      <div
        v-else-if="
          currentConversation?.status === ConversationStatus.NO_REPLY ||
          currentConversation?.status === ConversationStatus.CLOSE ||
          conversationCantAcceptNewMessages
        "
        class="tw-flex tw-justify-end"
      >
        <el-alert
          :closable="false"
          type="info"
          center
          class="alert-info tw-bg-gray-e tw-rounded-none tw-border tw-border-gray-5 tw-px-2.5"
        >
          {{
            currentConversation?.status === ConversationStatus.NO_REPLY
              ? t('chat.labels.noReplyPatientConversation')
              : t('chat.labels.closedPatientConversation')
          }}
        </el-alert>

        <AppButton
          v-if="isShowStartOverButton"
          type="primary"
          data-start-over-btn
          class="tw-w-[150px] tw-h-full tw-ml-2"
          @click="startOverConversation"
        >
          {{ t('pt.help.startOver') }}
        </AppButton>
      </div>

      <ChatBotInput
        v-else-if="
          currentConversation?.status === ConversationStatus.ASSIGNED_TO_BOT ||
          isScheduler
        "
        :loading="isSendMessageLoading"
        :conversation-id="currentConversation?.id ?? 0"
        :allowed-types="allowedTypes ?? []"
        @loaded="scrollToBottom"
        @send-message="sendUserMessage"
        @send-ac-results="sendAcResults"
      />

      <ChatPatientInput
        v-else-if="!isInputHide"
        :max-size="maxSize ?? null"
        :allowed-types="allowedTypes ?? []"
        :loading="isSendMessageLoading"
        :error="errorMessage"
        :scroll-callback="scrollToBottom"
        @vnode-mounted="scrollToBottom"
        @typing="onTyping"
        @send-message="sendUserMessage"
      />

      <PictureInPictureWindow
        :participants="conversationParticipants"
        @start="videoCallConfigsStore.startPictureInPictureMode(false)"
      />
    </div>
  </el-skeleton>

  <CallIncoming
    v-if="isCallIncomingShown"
    :screen-share-mode="isScreenSharingMode"
    @decline="onDecllineCall"
  />

  <VideoCall v-if="isVideoChatMode" />

  <el-dialog
    v-model="isFeedbackDialogOpened"
    destroy-on-close
    :show-close="false"
    class="sm:tw-w-1/2 tw-w-full dialog-base modal-body"
    center
  >
    <VideoCallFeedbackForm
      v-if="isFeedbackDialogOpened"
      :destroy-after-close="false"
      :is-loading="isLoading"
      @close="closeFeedbackDialog"
      @submit="onSubmitFeedback"
    />
  </el-dialog>
</template>
